import React, { useEffect, useMemo, useState } from 'react';
import { useStyletron } from 'baseui';
import { Button as BaseButton, SIZE } from 'baseui/button';
import { Drawer } from 'baseui/drawer';
import { FormControl } from 'baseui/form-control';
import { Heading, HeadingLevel } from 'baseui/heading';
import { ArrowRight } from 'baseui/icon';
import { Spinner } from 'baseui/spinner';
import { Textarea } from 'baseui/textarea';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonRow, EmptyFilePill, FilePill, ScrollToTopButton } from '@gtintel/platform-atoms';
import { ROUTES } from '@gtintel/platform-constants';
import { FeaturesContext } from '@gtintel/platform-context';
import { NPRequestStatus } from '@gtintel/platform-types';
import { NPRequestStatusColourMap, hexToRGB } from '@gtintel/platform-utils';
import ReportBuilder from '../../ProjectApp/ReportBuilder';
import { LegacyDocumentsContainer } from '../../ProjectApp/legacy/Documents';
import Documents from '../../components/Documents';
import DocumentsFlatList from '../../components/Documents/DocumentsFlatList';
import AssignmentChat from '../../components/LegacyChat/AssignmentButtonStateContainer';
import ReportBuilderMagicLink from '../../components/ReportBuilderMagicLink';
import { RequestStatusPill } from '../../components/RequestStatusIcon';
import RelatedAssignments from '../RelatedAssignments';
import AssignmentAttachments from '../RelatedAssignments/Attachments';
import { AssignmentContext, AssignmentDocumentsContext, AssignmentsNavigationContext } from '../context';
const cancelledAssignmentStatuses = [NPRequestStatus.AcceptedCancelled, NPRequestStatus.Cancelled, NPRequestStatus.Declined];
const activeAssignmentStatuses = [NPRequestStatus.Assigned, NPRequestStatus.Submitted, NPRequestStatus.Finished];
const AssignmentNavigationItem = ({
  navItem,
  activeItemId,
  onChange
}) => {
  const [css] = useStyletron();
  return <li className={css({
    backgroundColor: activeItemId === navItem.itemId ? 'black' : 'transparent',
    backgroundImage: activeItemId === navItem.itemId ? 'linear-gradient(0deg, rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92))' : 'none',
    borderLeft: '4px solid',
    borderColor: activeItemId === navItem.itemId ? 'black' : 'transparent',
    padding: '12px 12px 12px 24px',
    position: 'relative'
  })}>
      <a href={navItem.itemId} className={css({
      color: 'black',
      ':hover': {
        color: 'black'
      }
    })} onClick={e => onChange({
      event: e,
      item: {
        itemId: navItem.itemId
      }
    })}>
        <div>
          {navItem.title}
          {navItem.suffixElement}
        </div>
      </a>
    </li>;
};
const AssignmentNavigation = ({
  navItems,
  activeItemId,
  onChange
}) => {
  const [css] = useStyletron();
  return <nav>
      <ul className={css({
      listStyle: 'none',
      margin: 0,
      padding: 0
    })}>
        {navItems.map(navItem => <AssignmentNavigationItem key={navItem.itemId} navItem={navItem} activeItemId={activeItemId} onChange={onChange} />)}
      </ul>
    </nav>;
};
const AssignmentStatus = () => {
  const ctx = React.useContext(AssignmentContext);
  const [css] = useStyletron();
  const colour = NPRequestStatusColourMap[ctx.status()];
  let body;
  switch (ctx.status()) {
    case NPRequestStatus.Cancelled:
    case NPRequestStatus.AcceptedCancelled:
      body = <Heading styleLevel={5} color={colour} margin={0} fontWeight={'normal'}>
          Assignment cancelled
        </Heading>;
      break;
    case NPRequestStatus.Accepted:
      body = <Heading styleLevel={5} color={colour} margin={0}>
          You have accepted this request, but it has not yet been activated
        </Heading>;
      break;
    case NPRequestStatus.Declined:
      body = <Heading styleLevel={5} color={colour} margin={0}>
          You declined this request
        </Heading>;
      break;
    case NPRequestStatus.Submitted:
      body = <Heading styleLevel={5} color={colour} margin={0}>
          This assignment has been submitted but you can still edit the
          documents
        </Heading>;
      break;
    case NPRequestStatus.Finished:
      body = <Heading styleLevel={5} color={colour} margin={0}>
          This assignment is finished
        </Heading>;
      break;
    case NPRequestStatus.Sent:
    default:
      return;
  }
  return <div className={css({
    background: hexToRGB(colour, 0.1),
    border: `2px solid ${colour}`,
    borderRadius: '4px',
    marginBottom: '14px',
    padding: '14px'
  })}>
      {body}
    </div>;
};
const AssignmentHeader = () => {
  const navigation = React.useContext(AssignmentsNavigationContext);
  const ctx = React.useContext(AssignmentContext);
  const [css] = useStyletron();
  const colour = NPRequestStatusColourMap[ctx.status()];
  const show = ![NPRequestStatus.Sent, NPRequestStatus.Assigned].includes(ctx.status());
  let styles = {
    alignItems: 'center',
    borderRadius: '4px',
    color: 'black',
    display: 'flex',
    flexFlow: 'row nowrap',
    gap: '8px',
    justifyContent: 'space-between',
    marginBottom: '14px',
    padding: '8px 0',
    width: '100%',
    '@media (max-width: 768px)': {
      alignItems: 'flex-start',
      flexFlow: 'column'
    }
  };
  if (show) {
    styles = {
      ...styles,
      color: colour
    };
  }
  return <HeadingLevel>
      <AssignmentStatus />
      <div className={css(styles)}>
        <Heading>
          {ctx.name() ?? `Assignment ${navigation.assignmentId()}`}
        </Heading>
        <RequestStatusPill status={ctx.status()} />
      </div>
      <div>
        <p className={css({
        margin: '0'
      })}>
          <strong>Project ID:</strong> {ctx.projectId()}
        </p>
        <p className={css({
        margin: '0'
      })}>
          <strong>Assignment ID:</strong> {navigation.assignmentId()}
        </p>
      </div>
    </HeadingLevel>;
};
const Assignment = ({
  disableChat
}) => {
  const navigation = React.useContext(AssignmentsNavigationContext);
  const ctx = React.useContext(AssignmentContext);
  const documentsCtx = React.useContext(AssignmentDocumentsContext);
  const featuresCtx = React.useContext(FeaturesContext);
  const [css] = useStyletron();
  const [isOpen, setIsOpen] = React.useState(false);
  const [relatedAssignment, setRelatedAssignment] = React.useState(null);
  const [investigatorNotes, setInvestigatorNotes] = useState('');
  useEffect(() => {
    if (ctx.isQaAssignment()) {
      navigation.assignmentId() && ctx.getRelatedAssignments();
    }
  }, []);

  // Seed the notes editor from the saved notes once the assignment loads —
  // a rework round re-opens a previously-submitted assignment (status back
  // to Assigned), and the partner should adjust their notes, not retype.
  useEffect(() => {
    setInvestigatorNotes(ctx.investigatorNotes() ?? '');
  }, [navigation.assignmentId(), ctx.status()]);
  useMemo(() => {
    if (navigation.assignmentId() && ctx.projectId()) {
      documentsCtx.getAttachments(ctx.projectId());
    }
  }, [ctx.projectId()]);
  useEffect(() => {
    const assignment = ctx.relatedAssignments() && ctx.relatedAssignments().filter(a => a.id === ctx.getRelatedAssignment())[0];
    if (assignment) {
      setRelatedAssignment(assignment);
    }
  }, [ctx]);
  if (!navigation.assignmentId() || !ctx.status()) {
    return <section className={css({
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      height: 'calc(100vh - 168px)'
    })}>
        <Spinner />
      </section>;
  }
  const sections = [{
    title: 'Overview',
    itemId: 'overview'
  }, {
    title: 'Submit Assignment',
    itemId: 'upload'
  }, {
    title: 'Documents',
    itemId: 'documents'
  }];
  const onNavigate = ({
    event,
    item
  }) => {
    event.preventDefault();
    navigation.navigateToSection(item.itemId);
  };
  const container = css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start'
  });
  const nav = css({
    display: 'flex',
    flexFlow: 'column nowrap',
    height: 'calc(100vh - 88px)',
    alignItems: 'flex-start',
    margin: '0 2em 0 0',
    width: '190px',
    flexGrow: '0',
    flexShrink: '0',
    '@media (max-width: 600px)': {
      display: 'none'
    },
    position: 'sticky',
    top: '88px'
  });
  const chat = css({
    textAlign: 'center',
    width: '100%',
    padding: '1em'
  });
  const main = css({
    margin: '0 1em',
    minWidth: '360px',
    maxWidth: '1000px',
    flexGrow: '1'
  });
  const submitButtonContainer = css({
    marginTop: '1em'
  });
  let view;
  switch (navigation.assignmentSection()) {
    case 'documents':
      view = <React.Fragment>
          <AssignmentHeader />

          {!cancelledAssignmentStatuses.includes(ctx.status()) && <React.Fragment>
              <HeadingLevel>
                <Heading>Documents</Heading>
              </HeadingLevel>
              <Documents canUpload={ctx.status() === NPRequestStatus.Assigned && ctx.isLeadPartnerAssignment()} />
              <LegacyDocumentsContainer id={ctx.projectId()} />
            </React.Fragment>}
        </React.Fragment>;
      break;
    case 'upload':
      view = <React.Fragment>
          <AssignmentHeader />

          {!cancelledAssignmentStatuses.includes(ctx.status()) && <HeadingLevel>
              {featuresCtx.reportBuilder === 'on' ? <>
                  <ReportBuilderMagicLink projectId={Number(ctx.projectId())} disabled={!activeAssignmentStatuses.includes(ctx.status())} sendToken={true} />
                  <Heading>Uploading supporting documents</Heading>
                </> : <Heading>Upload Assignment</Heading>}

              {[NPRequestStatus.Assigned, NPRequestStatus.Submitted].includes(ctx.status()) && <React.Fragment>
                  <DocumentsFlatList canUpload={ctx.status() !== NPRequestStatus.Finished} canDelete isLeadPartnerAssignment={ctx.isLeadPartnerAssignment() || ctx.isQaAssignment()} />
                  &nbsp;
                  {ctx.relatedAssignments() && ctx.relatedAssignments().length > 0 && <HeadingLevel>
                        <Heading>Related Assignment Reports</Heading>
                        {ctx.relatedAssignments().map(assignment => {
                return <AssignmentAttachments key={assignment.id} attachments={assignment.documents || []} assignmentId={assignment.id} assignmentName={assignment.name} canDelete isLeadPartnerAssignment={ctx.isLeadPartnerAssignment() || ctx.isQaAssignment()} openRelatedDrawer={setIsOpen} />;
              })}
                      </HeadingLevel>}
                </React.Fragment>}
              {ctx.status() === NPRequestStatus.Assigned && <React.Fragment>
                  <FormControl label="Investigator Notes" caption="Add any additional context about your findings, observations, or issues encountered during the assignment.">
                    <Textarea value={investigatorNotes} onChange={e => setInvestigatorNotes(e.currentTarget.value)} placeholder="e.g. I was unable to access the premises due to..." overrides={{
                Input: {
                  style: {
                    minHeight: '120px'
                  }
                }
              }} />
                  </FormControl>
                  <div className={submitButtonContainer}>
                    <ButtonRow shrinkDirection="row" flexPosition="flex-end">
                      <Button size={SIZE.default} onClick={() => ctx.submit(investigatorNotes)} disabled={ctx.documents() && ctx.documents().length === 0}>
                        Submit
                      </Button>
                    </ButtonRow>
                  </div>
                </React.Fragment>}
              {[NPRequestStatus.Submitted, NPRequestStatus.Finished].includes(ctx.status()) && ctx.investigatorNotes() && <FormControl label="Investigator Notes">
                    <Textarea value={ctx.investigatorNotes()} readOnly overrides={{
              Input: {
                style: {
                  minHeight: '120px'
                }
              }
            }} />
                  </FormControl>}
            </HeadingLevel>}

          {ctx.relatedAssignments() && <Drawer isOpen={isOpen} autoFocus animate onClose={() => {
          ctx.setRelatedAssignment(null);
          setIsOpen(false);
        }} overrides={{
          Root: {
            style: {
              zIndex: '100'
            }
          }
        }}>
              <div>
                <HeadingLevel>
                  <Heading>
                    {relatedAssignment && (relatedAssignment.name ?? `Assignment #${relatedAssignment.id}`)}
                  </Heading>
                  <DocumentsFlatList canUpload={true} canDelete isLeadPartnerAssignment={ctx.isLeadPartnerAssignment() || ctx.isQaAssignment()} attachments={relatedAssignment && relatedAssignment.documents} />
                </HeadingLevel>
              </div>
            </Drawer>}
        </React.Fragment>;
      break;
    case 'overview':
    default:
      view = <React.Fragment>
          <AssignmentHeader />

          <div className={css({
          marginBottom: '24px'
        })}>
            {ctx.status() === NPRequestStatus.Sent && <ButtonRow shrinkDirection="row" flexPosition="flex-end">
                <Button kind="success" size={SIZE.default} onClick={() => ctx.accept()}>
                  Accept
                </Button>
                <Button kind="error" size={SIZE.default} onClick={() => ctx.decline()}>
                  Decline
                </Button>
              </ButtonRow>}
          </div>

          {!cancelledAssignmentStatuses.includes(ctx.status()) && <React.Fragment>
              <section className={css({
            display: 'grid',
            gap: '8px',
            gridTemplateColumns: '1fr 1fr',
            marginBottom: '48px',
            '@media (max-width: 768px)': {
              gridTemplateColumns: '1fr'
            }
          })}>
                {ctx.reportTemplate() ? <FilePill file={ctx.reportTemplate()} linkText="Report Template" /> : <EmptyFilePill linkText="Report Template missing. Contact GTI" />}
                {ctx.sampleReport() && <FilePill file={ctx.sampleReport()} linkText="Example" />}
              </section>
              <ReportBuilder hideDetails={true} />
              {ctx.relatedAssignments() && ctx.relatedAssignments().length > 0 && <RelatedAssignments />}
            </React.Fragment>}
        </React.Fragment>;
      break;
  }
  let chatView = null;
  if (!disableChat && ![NPRequestStatus.Cancelled.toString(), NPRequestStatus.AcceptedCancelled.toString(), NPRequestStatus.Declined.toString()].includes(ctx.status().toString())) {
    chatView = <AssignmentChat projectId={ctx.projectId()} />;
  }
  return <div className={container} data-testid="assignment">
      <div className={nav}>
        <AssignmentNavigation navItems={sections} activeItemId={navigation.assignmentSection() || 'overview'} onChange={onNavigate} />
        <div className={chat}>{chatView}</div>
        <div className={css({
        alignItems: 'center',
        display: 'flex',
        flexFlow: 'column',
        gap: '14px',
        marginTop: 'auto'
      })}>
          {ctx.isLeadPartnerAssignment() && !cancelledAssignmentStatuses.includes(ctx.status()) && <BaseButton $as={'a'} href={`${ROUTES.PARTNER_PROJECT_DETAIL}/${ctx.projectId()}`} endEnhancer={() => <ArrowRight size={24} title="" />} kind="secondary">
                Open Project
              </BaseButton>}

          <Button onClick={() => navigation.navigateToIndex()} overrides={{
          Root: {
            style: {
              marginBottom: '14px'
            }
          }
        }} title="Go Back To Assignments List">
            <FontAwesomeIcon icon={faArrowLeft} size="lg" className={css({
            color: 'white !important'
          })} />
            &nbsp; Go Back To List
          </Button>
        </div>
      </div>
      <div className={main}>{view}</div>
      <ScrollToTopButton />
    </div>;
};
export default Assignment;